@let financialAccount = financialAccount$ | async;
@let createdBy = $safeNavigationMigration(financialAccount?.createdBy?.id) | teamMemberName | async;
@let updatedBy = $safeNavigationMigration(financialAccount?.updatedBy?.id) | teamMemberName | async;

@if (!financialAccount) {
  <app-details-page-content-placeholder [singleColumn]="true" />
} @else {
  <div class="flex gap-5">
    <div class="bg-white rounded-lg px-6 py-5 mt-5 flex-1">
      <div class="grid gap-x-16 gap-y-5 w-full xl:w-3/3 lg:grid-cols-3">
        <app-details-item-id label="Financial Account ID" [id]="financialAccount.id" />
        <app-details-item-id
          label="Business Account ID"
          [link]="['/app/business-accounts', financialAccount.businessAccountId]"
          [id]="financialAccount.businessAccountId"
        />
        <app-details-item-id
          label="Parent ID"
          [link]="['/app', 'financial-accounts', financialAccount.parentId ?? '']"
          [id]="financialAccount.parentId"
        />
        <app-details-item-id label="Account Holder ID" [id]="financialAccount.accountHolderId" />
        <app-details-item label="Account Holder Type" [value]="financialAccount.accountHolderType | titlecase" />
        <app-details-item-id label="Sponsor Financial Account ID" [id]="financialAccount.sponsorFinancialAccountId" />
      </div>

      <div class="separator"></div>

      <div class="grid gap-x-16 gap-y-5 w-full xl:w-3/3 lg:grid-cols-3">
        <app-details-item label="Account Type" [value]="financialAccount.type" />
        <app-details-item label="Account Subtype" [value]="financialAccount.subtype" />
        <app-details-item label="Account Category" [value]="financialAccount.category" />
        <app-details-item label="Financial Account Name" [value]="financialAccount.name" />
        <app-details-item label="Financial Institution ID" [value]="financialAccount.financialInstitutionShortId" />
        <app-details-item label="Financial Institution Name" [value]="financialAccount.financialInstitutionName" />
        <app-details-item label="Currency" [value]="financialAccount.currency" />
        <app-details-item label="Verification Status" [value]="financialAccount.verificationStatus | titlecase" />
        <app-details-item label="Role" [value]="financialAccount.role" />
        <app-details-item label="Token" [value]="financialAccount.token" />
      </div>

      <div class="separator"></div>

      @if (financialAccount.card) {
        <div class="bg-gray-100 p-2 rounded-lg">
          <div class="flex text-sm items-center gap-2 mb-3">
            <rkt-icon class="text-xl rkt-icon" name="card" />
            CARD
          </div>
          <div class="grid gap-x-16 gap-y-5 w-full xl:w-3/3 lg:grid-cols-3">
            <app-details-item label="Name on Card" [value]="financialAccount.card.fullNameOnCard" />
            <app-details-item label="Card Number" [value]="financialAccount.lastFourNumbers" />
            <app-details-item label="Card Exp. Date" [value]="financialAccount.card.expiry" />

            <app-details-item label="Address" [value]="financialAccount.card.billingAddress.addressLine1">
              @if (financialAccount.card.billingAddress.addressLine1) {
                <div>{{ financialAccount.card.billingAddress.addressLine2 }}</div>
                <div>{{ financialAccount.card.billingAddress.city }}, {{ financialAccount.card.billingAddress.state }}</div>
                <div>{{ financialAccount.card.billingAddress.postalCode }} {{ financialAccount.card.billingAddress.country }}</div>
              }
            </app-details-item>
          </div>
        </div>
        <div class="separator"></div>
      }

      @if (financialAccount.bankAccount) {
        <div class="bg-gray-100 p-2 rounded-lg">
          <div class="flex text-sm items-center gap-2 mb-3">
            <rkt-icon class="text-xl rkt-icon" name="bank" />
            BANK
          </div>
          <div class="grid gap-x-16 gap-y-5 w-full xl:w-3/3 lg:grid-cols-3">
            <app-details-item label="Name on Account" [value]="financialAccount.bankAccount.nameOnAccount" />
            <app-details-item label="Routing No." [value]="financialAccount.bankAccount.routingNo" />
            <app-details-item label="Account Number" [value]="financialAccount.lastFourNumbers" />
            <app-details-item label="Bank Name" [value]="financialAccount.bankAccount.bankName" />

            <app-details-item label="Address" [value]="$safeNavigationMigration(financialAccount.bankAccount.billingAddress?.addressLine1)">
              @if (financialAccount.bankAccount.billingAddress?.addressLine1) {
                <div>{{ financialAccount.bankAccount.billingAddress?.addressLine2 }}</div>
                <div>
                  {{ financialAccount.bankAccount.billingAddress?.city }},
                  {{ financialAccount.bankAccount.billingAddress?.state }}
                </div>
                <div>
                  {{ financialAccount.bankAccount.billingAddress?.postalCode }}
                  {{ financialAccount.bankAccount.billingAddress?.country }}
                </div>
              }
            </app-details-item>
          </div>
        </div>
        <div class="separator"></div>
      }

      <div class="grid gap-x-16 gap-y-5 w-full xl:w-2/3 lg:grid-cols-2">
        <app-details-item label="Created At" [value]="financialAccount.createdAt | dateFormat" />
        <app-details-item label="Created By" [value]="createdBy" />
        <app-details-item label="Updated At" [value]="financialAccount.updatedAt | dateFormat" />
        <app-details-item label="Updated By" [value]="updatedBy" />
      </div>
    </div>
  </div>

  @if (financialAccount.restrictions) {
    <div class="entity-details-container mt-3">
      <div class="text-font-primary pb-3 text-base font-bold flex justify-between">
        Restrictions

        <button
          appRemoveFocus
          class="p-2 text-lg text-secondary rounded-full transition-colors hover:bg-gray-100"
          title="Edit Restrictions"
          (click)="onEditBtnClick(financialAccount.restrictions, financialAccount.id)"
        >
          <rkt-icon name="edit" />
        </button>
      </div>
      <div class="financial-account-restrictions">
        <rkt-table
          [rows]="financialAccount.restrictions"
          [columns]="restrictionsColumns"
          [count]="financialAccount.restrictions.length"
          [messages]="{ emptyMessage: 'No restrictions Found' }"
          emptyValuePlaceholder="--"
          dateTimeFormat="MM/dd/yy HH:mm"
        />
      </div>
    </div>
  }
}
